// Saturated brand colors for the per-offering pill — used in the patient
// profile header (offering badge) AND inside the Treatment section of
// PatientSummary (procedure type badge on each procedure row).
//
// Mapped from the design system's per-offering scheme (see
// everself-design-system/01-foundation/tokens/color → "Internal-only per-offering").
//
// Pair with Warm White text and a filled Badge variant. Off-canon: gray neutral
// fills, light tints, or pastel variants — the offering is a saturated identity
// signal, not chrome.

export type Offering = 'ESG' | 'SIS' | 'TORe' | 'IGB';

export const OFFERING_COLORS: Record<Offering, string> = {
  ESG: '#3180e3', // Slate Blue
  SIS: '#679773', // Sage
  TORe: '#3d8b8b', // Teal
  IGB: '#ff7044', // Mid Orange
};

/**
 * Extract the offering code (e.g. "ESG", "SIS") from an Appointment's
 * appointmentType display string. Returns null if the offering isn't one of
 * the canonical codes — pill should not render in that case.
 *
 * Production display strings look like:
 *   "Your Everself SIS"
 *   "Your Everself TORe+"
 *   "Your Everself ESG"
 *   "Your gastric balloon"
 *   "Your gastric balloon removal"
 * We search for the offering keyword anywhere in the string,
 * case-insensitively. Order matters: TORe must match before
 * generic words.
 */
export function getOfferingCode(appointmentTypeDisplay: string | undefined): Offering | null {
  if (!appointmentTypeDisplay) return null;
  const s = appointmentTypeDisplay;
  if (/\bESG\b/i.test(s)) return 'ESG';
  if (/\bTORe?\b/i.test(s)) return 'TORe';
  if (/\bSIS\b/i.test(s)) return 'SIS';
  if (/\bIGB\b/i.test(s) || /gastric\s+balloon/i.test(s)) return 'IGB';
  return null;
}

/**
 * Return whether the appointmentType display indicates an IGB removal —
 * those procedures are excluded when picking the "primary" procedure for the
 * per-offering pill.
 *
 * Production strings: "Your gastric balloon removal" / "IGB removal procedure".
 */
export function isIgbRemoval(appointmentTypeDisplay: string | undefined): boolean {
  if (!appointmentTypeDisplay) return false;
  return /gastric\s+balloon\s+removal|IGB\s+removal/i.test(appointmentTypeDisplay);
}
